import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Connector } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import { getAllActiveUsers } from "@/services/userService";
import { TEST_ADMIN_USER_ID } from "@/lib/constants";
import { sendPartnerSharedNotification } from "@/services/notificationService";
import { updateChannelPartner } from "@/services/channelPartnerService";
import { toast } from "sonner";
import { UserPlus, Share2, ChevronDown, Users, X } from "lucide-react";

interface SharePartnerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partner: Connector;
}

export function SharePartnerDialog({ open, onOpenChange, partner }: SharePartnerDialogProps) {
  const { user } = useAuth();
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [shareWithAll, setShareWithAll] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<Array<{ id?: string; userId?: string; name: string }>>([]);
  const [isSharing, setIsSharing] = useState(false);

  // Load users from Firebase
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersData = await getAllActiveUsers();
        setAvailableUsers(usersData.filter(u => (u.userId || u.id) !== TEST_ADMIN_USER_ID));
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };
    loadUsers();
  }, []);

  // Reset when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedUsers([]);
      setNote("");
      setShareOpen(false);
      setShareWithAll(false);
    }
  }, [open]);

  // Check if user is already shared
  const isAlreadyShared = (userId: string) => {
    return partner.sharedWith?.includes(userId) || partner.createdBy === userId;
  };

  // Show all users except the creator
  const usersToShow = availableUsers.filter(tm => {
    const userId = tm.userId || tm.id;
    return partner.createdBy !== userId;
  });

  const handleToggleUser = (userId: string) => {
    // If user is already shared, remove them instead of adding to selectedUsers
    if (isAlreadyShared(userId)) {
      handleRemoveSharedUser(userId);
      return;
    }
    
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
    setShareWithAll(false);
  };

  const handleShareWithAll = () => {
    if (shareWithAll) {
      setSelectedUsers([]);
      setShareWithAll(false);
    } else {
      // Only select users who aren't already shared
      const newUsers = usersToShow
        .filter(m => !isAlreadyShared(m.userId || m.id))
        .map(m => m.userId || m.id);
      setSelectedUsers(newUsers);
      setShareWithAll(true);
    }
  };
  const handleRemoveSharedUser = async (userIdToRemove: string) => {
    try {
      const updatedSharedWith = (partner.sharedWith || []).filter(id => id !== userIdToRemove);
      
      // If no users left, explicitly pass undefined to trigger deleteField in service
      await updateChannelPartner(partner.id, {
        sharedWith: updatedSharedWith.length > 0 ? updatedSharedWith : undefined,
      });

      const removedUser = availableUsers.find(u => (u.userId || u.id) === userIdToRemove);
      toast.success(`Removed ${removedUser?.name || 'user'} from shared access`);
    } catch (error) {
      console.error('Error removing shared user:', error);
      toast.error("Failed to remove user");
    }
  };
  const handleShare = async () => {
    if (selectedUsers.length === 0) {
      toast.error("Please select at least one user to share with");
      return;
    }

    setIsSharing(true);

    try {
      // Get names of selected users
      const sharedUserNames = availableUsers
        .filter(tm => selectedUsers.includes(tm.userId || tm.id))
        .map(tm => tm.name)
        .join(", ");

      // Update partner's sharedWith array
      const updatedSharedWith = [...(partner.sharedWith || []), ...selectedUsers];

      await updateChannelPartner(partner.id, {
        sharedWith: updatedSharedWith,
      });

      // Send FCM notifications
      try {
        await sendPartnerSharedNotification(
          selectedUsers,
          partner,
          user?.name || 'A colleague',
          note
        );
      } catch (error) {
        console.error('Error sending notifications:', error);
        // Don't fail the whole operation if notifications fail
      }

      toast.success(`Partner shared with ${sharedUserNames}`);
      onOpenChange(false);
    } catch (error) {
      console.error('Error sharing partner:', error);
      toast.error("Failed to share partner");
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Share Channel Partner
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {usersToShow.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No other users available</p>
            </div>
          ) : (
            <>
              {/* Share with colleagues collapsible */}
              <Collapsible open={shareOpen} onOpenChange={setShareOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" className="w-full justify-between" type="button">
                    <span className="flex items-center gap-2">
                      <UserPlus className="h-4 w-4" />
                      <span>
                        {selectedUsers.length > 0 
                          ? `Selected ${shareWithAll ? 'all' : selectedUsers.length} colleague${selectedUsers.length > 1 ? 's' : ''}`
                          : 'Select colleagues to share'
                        }
                      </span>
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${shareOpen ? 'rotate-180' : ''}`} />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-3 space-y-2">
                  {/* Share with all checkbox */}
                  <div 
                    className="flex items-center gap-3 p-3 rounded-xl border cursor-pointer hover:bg-secondary/50"
                    onClick={handleShareWithAll}
                  >
                    <Checkbox 
                      checked={shareWithAll} 
                      onCheckedChange={handleShareWithAll}
                    />
                    <span className="text-sm font-medium">Share with all</span>
                  </div>

                  {/* Individual user checkboxes in grid */}
                  <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                    {usersToShow.map((member) => {
                      const userId = member.userId || member.id;
                      const alreadyShared = isAlreadyShared(userId);
                      return (
                        <div 
                          key={userId}
                          className={`flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer hover:bg-secondary/50 ${
                            alreadyShared ? 'bg-secondary/30' : ''
                          }`}
                          onClick={() => handleToggleUser(userId)}
                        >
                          <Checkbox 
                            checked={alreadyShared || selectedUsers.includes(userId)}
                            onCheckedChange={() => handleToggleUser(userId)}
                          />
                          <span className="text-sm truncate">{member.name.split(' ')[0]}</span>
                        </div>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Optional note */}
              <div className="space-y-2">
                <Label htmlFor="note">Add a note (optional)</Label>
                <Textarea
                  id="note"
                  placeholder="Add a message for your colleagues..."
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={isSharing}
          >
            Cancel
          </Button>
          {usersToShow.length > 0 && (
            <Button 
              onClick={handleShare}
              disabled={selectedUsers.length === 0 || isSharing}
            >
              {isSharing ? "Sharing..." : "Share"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
